import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  assetTypeSchema,
  currentSchema,
  distanceSchema,
  humiditySchema,
  occupancyExpectationSchema,
  occupancyProblem,
  temperatureSchema,
  type Asset,
  type CreateAssetRequest,
  type UpdateAssetRequest,
} from "@/contracts";
import { z } from "zod";

import {
  createAssetMutation,
  organizationOptionsQuery,
  siteOptionsQuery,
  updateAssetMutation,
} from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { ASSET_TYPE_LABELS, OCCUPANCY_LABELS } from "./asset-labels";
import { exampleFor, monitors, type Monitored, type RangeField } from "./asset-monitoring";

/** Monday first, matching ISO-8601 and the way a week is written here. */
const WEEKDAY_VALUES = ["1", "2", "3", "4", "5", "6", "7"] as const;

/** Empty means "no bound set", which is not the same number as zero. */
const optionalNumber = z
  .string()
  .trim()
  .refine((value) => value === "" || Number.isFinite(Number(value)), {
    message: "Enter a number, or leave it empty.",
  });

const assetFormFields = z.object({
  organizationId: z.string(),
  siteId: z.string(),
  name: z.string().trim().min(2, "Enter a name of at least 2 characters.").max(120),
  type: assetTypeSchema,
  targetTempMinC: optionalNumber,
  targetTempMaxC: optionalNumber,
  targetHumidityMin: optionalNumber,
  targetHumidityMax: optionalNumber,
  targetDistanceMinMm: optionalNumber,
  targetDistanceMaxMm: optionalNumber,
  targetCurrentMinA: optionalNumber,
  targetCurrentMaxA: optionalNumber,
  occupancy: occupancyExpectationSchema,
  // Checkbox groups hand back strings, and the conversion happens once on
  // submit rather than in seven places.
  occupancyDays: z.array(z.string()),
  occupancyStart: z.string(),
  occupancyEnd: z.string(),
  notes: z.string().trim().max(1_000),
});

type AssetFormValues = z.infer<typeof assetFormFields>;

interface RangeCheck {
  readonly monitored: Monitored;
  readonly min: RangeField;
  readonly max: RangeField;
  readonly bound: z.ZodType<number>;
  readonly incomplete: string;
  readonly outside: string;
}

/** The three ranges as data, so the checks and the fields cannot drift apart. */
const RANGES: readonly RangeCheck[] = [
  {
    monitored: "TEMPERATURE",
    min: "targetTempMinC",
    max: "targetTempMaxC",
    bound: temperatureSchema,
    incomplete: "Give both ends of the temperature range, or neither.",
    outside: "Temperature runs from -80 to 40 °C.",
  },
  {
    monitored: "HUMIDITY",
    min: "targetHumidityMin",
    max: "targetHumidityMax",
    bound: humiditySchema,
    incomplete: "Give both ends of the humidity range, or neither.",
    outside: "Humidity runs from 0 to 100 %.",
  },
  {
    monitored: "DISTANCE",
    min: "targetDistanceMinMm",
    max: "targetDistanceMaxMm",
    bound: distanceSchema,
    incomplete: "Give both ends of the distance range, or neither.",
    outside: "The sensor only reads from 300 to 12000 mm.",
  },
  {
    monitored: "CURRENT",
    min: "targetCurrentMinA",
    max: "targetCurrentMaxA",
    bound: currentSchema,
    incomplete: "Give both ends of the current range, or neither.",
    outside: "Current runs from 0 to 250 A.",
  },
];

/**
 * The same rules the contract enforces, checked before the round trip: a
 * range needs both ends, and an inverted one would make either every reading
 * an excursion or none at all.
 *
 * Judged against the values the chosen asset type actually asks for, so a
 * field the form is not showing cannot fail validation. An error on a
 * question nobody was asked is unanswerable.
 */
const assetFormSchema = assetFormFields.superRefine((raw, ctx) => {
  const value = onlyMonitored(raw);

  for (const range of RANGES) {
    const min = value[range.min];
    const max = value[range.max];

    if ((min === "") !== (max === "")) {
      ctx.addIssue({ code: "custom", message: range.incomplete, path: [range.max] });
    }

    for (const field of [range.min, range.max] as const) {
      if (!inBounds(value[field], range.bound)) {
        ctx.addIssue({ code: "custom", message: range.outside, path: [field] });
      }
    }

    if (!below(min, max)) {
      ctx.addIssue({
        code: "custom",
        message: "The minimum must be below the maximum.",
        path: [range.min],
      });
    }
  }

  const problem = occupancyProblem(toOccupancySettings(value));

  if (problem) {
    ctx.addIssue({ code: "custom", message: problem.message, path: [problem.path] });
  }
});

/**
 * The values the chosen asset type actually asks for, with the rest blanked.
 *
 * Nothing invisible may decide anything. Somebody who typed a freezer's
 * temperature and then changed the type to a tank has not made a mistake
 * worth an error message, the field is no longer on screen to correct, and
 * the number must not reach the API either. Same argument as the
 * occupancy hours below, applied to every range.
 */
function onlyMonitored(value: AssetFormValues): AssetFormValues {
  const asked = (what: Monitored, raw: string) => (monitors(value.type, what) ? raw : "");

  return {
    ...value,
    targetTempMinC: asked("TEMPERATURE", value.targetTempMinC),
    targetTempMaxC: asked("TEMPERATURE", value.targetTempMaxC),
    targetHumidityMin: asked("HUMIDITY", value.targetHumidityMin),
    targetHumidityMax: asked("HUMIDITY", value.targetHumidityMax),
    targetDistanceMinMm: asked("DISTANCE", value.targetDistanceMinMm),
    targetDistanceMaxMm: asked("DISTANCE", value.targetDistanceMaxMm),
    targetCurrentMinA: asked("CURRENT", value.targetCurrentMinA),
    targetCurrentMaxA: asked("CURRENT", value.targetCurrentMaxA),
    // `toOccupancySettings` drops the days and hours of a state that does not
    // use them, so resetting the state is enough to drop all four.
    ...(monitors(value.type, "OCCUPANCY") ? {} : { occupancy: "UNMONITORED" as const }),
  };
}

/** The form's strings, as the contract's helper expects to see them. */
function toOccupancySettings(value: AssetFormValues) {
  const scheduled = value.occupancy === "SCHEDULED";

  return {
    occupancy: value.occupancy,
    occupancyDays: scheduled ? value.occupancyDays.map(Number) : [],
    // Hours belonging to a state that does not use them are dropped rather
    // than reported as a conflict: somebody who filled in times and then
    // changed their mind has not made a mistake worth an error message.
    occupancyStart: scheduled && value.occupancyStart ? value.occupancyStart : null,
    occupancyEnd: scheduled && value.occupancyEnd ? value.occupancyEnd : null,
  };
}

/**
 * The occupancy part of the request body.
 *
 * Omits every field the chosen state does not use, rather than sending
 * nulls: `exactOptionalPropertyTypes` makes absence and null different
 * things here too, and the API refuses hours that belong to no schedule.
 */
function occupancyBody(settings: ReturnType<typeof toOccupancySettings>) {
  if (settings.occupancy === "UNMONITORED") {
    return {};
  }

  if (settings.occupancy === "NEVER") {
    return { occupancy: "NEVER" } as const;
  }

  return {
    occupancy: "SCHEDULED",
    occupancyDays: settings.occupancyDays,
    ...(settings.occupancyStart ? { occupancyStart: settings.occupancyStart } : {}),
    ...(settings.occupancyEnd ? { occupancyEnd: settings.occupancyEnd } : {}),
  } as const;
}

/**
 * The whole asset as a patch, for an edit.
 *
 * Every field is sent, and an emptied one as `null` rather than omitted: on
 * a PATCH, absent means "leave it alone", and somebody who cleared a range
 * or changed the type to one that does not ask for it meant to remove it.
 */
function updateBody(values: AssetFormValues): UpdateAssetRequest {
  const settings = toOccupancySettings(values);
  const number = (raw: string) => (raw ? Number(raw) : null);

  return {
    name: values.name,
    type: values.type,
    targetTempMinC: number(values.targetTempMinC),
    targetTempMaxC: number(values.targetTempMaxC),
    targetHumidityMin: number(values.targetHumidityMin),
    targetHumidityMax: number(values.targetHumidityMax),
    targetDistanceMinMm: number(values.targetDistanceMinMm),
    targetDistanceMaxMm: number(values.targetDistanceMaxMm),
    targetCurrentMinA: number(values.targetCurrentMinA),
    targetCurrentMaxA: number(values.targetCurrentMaxA),
    occupancy: settings.occupancy,
    occupancyDays: settings.occupancyDays,
    occupancyStart: settings.occupancyStart,
    occupancyEnd: settings.occupancyEnd,
    notes: values.notes || null,
  };
}

function formValuesOf(asset: Asset): AssetFormValues {
  const text = (value: number | null) => (value === null ? "" : String(value));

  return {
    organizationId: asset.organizationId,
    siteId: asset.siteId,
    name: asset.name,
    type: asset.type,
    targetTempMinC: text(asset.targetTempMinC),
    targetTempMaxC: text(asset.targetTempMaxC),
    targetHumidityMin: text(asset.targetHumidityMin),
    targetHumidityMax: text(asset.targetHumidityMax),
    targetDistanceMinMm: text(asset.targetDistanceMinMm),
    targetDistanceMaxMm: text(asset.targetDistanceMaxMm),
    targetCurrentMinA: text(asset.targetCurrentMinA),
    targetCurrentMaxA: text(asset.targetCurrentMaxA),
    occupancy: asset.occupancy,
    occupancyDays: asset.occupancyDays.map(String),
    occupancyStart: asset.occupancyStart ?? "",
    occupancyEnd: asset.occupancyEnd ?? "",
    notes: asset.notes ?? "",
  };
}

const EMPTY_FORM: AssetFormValues = {
  organizationId: "",
  siteId: "",
  name: "",
  type: "REFRIGERATOR",
  targetTempMinC: "",
  targetTempMaxC: "",
  targetHumidityMin: "",
  targetHumidityMax: "",
  targetDistanceMinMm: "",
  targetDistanceMaxMm: "",
  targetCurrentMinA: "",
  targetCurrentMaxA: "",
  occupancy: "UNMONITORED",
  occupancyDays: [],
  occupancyStart: "",
  occupancyEnd: "",
  notes: "",
};

function inBounds(raw: string, schema: z.ZodType<number>): boolean {
  return raw === "" || schema.safeParse(Number(raw)).success;
}

function below(min: string, max: string): boolean {
  return min === "" || max === "" || Number(min) < Number(max);
}

/**
 * Creates an asset, or edits `asset` when one is given.
 *
 * The edit keeps the site fixed: devices take their site from the asset, so
 * moving one is a relocation rather than a correction (see the contract).
 */
export function AssetFormDialog({
  open,
  asset,
  onClose,
}: {
  open: boolean;
  asset?: Asset;
  onClose: () => void;
}) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const editing = asset !== undefined;

  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const organizations = useQuery({
    ...organizationOptionsQuery(client),
    enabled: isSuperAdmin && !editing,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AssetFormValues>({
    resolver: zodResolver(assetFormSchema),
    defaultValues: asset ? formValuesOf(asset) : EMPTY_FORM,
  });

  const occupancy = useWatch({ control, name: "occupancy" });

  // What the asset is decides which questions are worth asking: a tank has no
  // humidity to hold and a meeting room has no minimum temperature.
  const type = useWatch({ control, name: "type" });
  const asks = (what: Monitored) => monitors(type, what);
  const example = (field: RangeField) => exampleFor(type, field) ?? "";

  const selectedOrganization = useWatch({ control, name: "organizationId" });
  const siteId = useWatch({ control, name: "siteId" });
  // For a plain admin this stays undefined and the API scopes by session.
  const tenant = isSuperAdmin ? selectedOrganization || undefined : undefined;
  const tenantChosen = !isSuperAdmin || Boolean(selectedOrganization);

  const sites = useQuery({
    ...siteOptionsQuery(client, tenant),
    enabled: open && tenantChosen && !editing,
  });

  const createMutation = useMutation({
    ...createAssetMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["assets"] });
      // An asset changes its site's asset count.
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
  });

  const updateMutation = useMutation({
    ...updateAssetMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["assets"] });
      // Devices, incidents and rules show the asset's name next to their own.
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      await queryClient.invalidateQueries({ queryKey: ["incidents"] });
      await queryClient.invalidateQueries({ queryKey: ["alert-rules"] });
    },
  });

  const mutation = editing ? updateMutation : createMutation;

  function close() {
    reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = handleSubmit(async (raw) => {
    const values = onlyMonitored(raw);

    if (asset) {
      try {
        await updateMutation.mutateAsync({ id: asset.id, body: updateBody(values) });
        close();
      } catch (error) {
        showFieldErrors(error);
      }

      return;
    }

    if (isSuperAdmin && !values.organizationId) {
      setError("organizationId", { message: t("chooseOrgOwnsAsset") });

      return;
    }

    if (!values.siteId) {
      setError("siteId", { message: t("chooseSiteForAsset") });

      return;
    }

    const body: CreateAssetRequest = {
      siteId: values.siteId,
      name: values.name,
      type: values.type,
      ...(values.targetTempMinC ? { targetTempMinC: Number(values.targetTempMinC) } : {}),
      ...(values.targetTempMaxC ? { targetTempMaxC: Number(values.targetTempMaxC) } : {}),
      ...(values.targetHumidityMin ? { targetHumidityMin: Number(values.targetHumidityMin) } : {}),
      ...(values.targetHumidityMax ? { targetHumidityMax: Number(values.targetHumidityMax) } : {}),
      ...(values.targetDistanceMinMm
        ? { targetDistanceMinMm: Number(values.targetDistanceMinMm) }
        : {}),
      ...(values.targetDistanceMaxMm
        ? { targetDistanceMaxMm: Number(values.targetDistanceMaxMm) }
        : {}),
      ...(values.targetCurrentMinA ? { targetCurrentMinA: Number(values.targetCurrentMinA) } : {}),
      ...(values.targetCurrentMaxA ? { targetCurrentMaxA: Number(values.targetCurrentMaxA) } : {}),
      ...occupancyBody(toOccupancySettings(values)),
      ...(values.notes ? { notes: values.notes } : {}),
      ...(values.organizationId ? { organizationId: values.organizationId } : {}),
    };

    try {
      await createMutation.mutateAsync(body);
      close();
    } catch (error) {
      showFieldErrors(error);
    }
  });

  function showFieldErrors(error: unknown) {
    if (error instanceof ApiError) {
      for (const [field, message] of Object.entries(error.fieldErrors())) {
        if (field in assetFormFields.shape) {
          setError(field as keyof AssetFormValues, { message });
        }
      }
    }
  }

  return (
    <FormDialog
      open={open}
      title={editing ? t("dialogEditAssetTitle") : t("dialogAddAssetTitle")}
      description={editing ? t("dialogEditAssetDescription") : t("dialogAddAssetDescription")}
      submitLabel={editing ? t("saveAsset") : t("createAsset")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        {isSuperAdmin && !editing ? (
          <SelectField
            label={tCommon("organization")}
            required
            {...(errors.organizationId?.message === undefined
              ? {}
              : { error: errors.organizationId.message })}
            value={selectedOrganization}
            onChange={(value) =>
              setValue("organizationId", value, { shouldValidate: true, shouldDirty: true })
            }
            data={[
              { value: "", label: t("chooseOrganization") },
              ...(organizations.data?.data ?? []).map((organization) => ({
                value: organization.id,
                label: organization.name,
              })),
            ]}
          />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={tCommon("name")}
            required
            placeholder={t("placeholderAssetName")}
            {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
            {...register("name")}
          />

          <SelectField
            label={tCommon("type")}
            required
            value={type}
            onChange={(value) =>
              setValue("type", value as AssetFormValues["type"], {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            data={assetTypeSchema.options.map((option) => ({
              value: option,
              label: ASSET_TYPE_LABELS[option],
            }))}
          />

          {asset ? (
            <SelectField
              label={tCommon("site")}
              disabled
              hint={t("hintAssetSiteFixed")}
              value={asset.siteId}
              onChange={() => undefined}
              data={[{ value: asset.siteId, label: asset.siteName }]}
            />
          ) : (
            <SelectField
              label={tCommon("site")}
              required
              disabled={!tenantChosen}
              {...(errors.siteId?.message === undefined ? {} : { error: errors.siteId.message })}
              value={siteId}
              onChange={(value) =>
                setValue("siteId", value, { shouldValidate: true, shouldDirty: true })
              }
              data={[
                { value: "", label: "Choose a site" },
                ...(sites.data?.data ?? []).map((site) => ({
                  value: site.id,
                  label: site.name,
                })),
              ]}
            />
          )}
        </div>

        {/* Only the ranges this kind of asset is judged on, and the placeholders
            are examples rather than defaults: an asset can be monitored for the
            record with no range enforced against it, so a number that arrived by
            itself would be a threshold nobody chose (ADR-045). */}
        {asks("TEMPERATURE") || asks("HUMIDITY") || asks("DISTANCE") || asks("CURRENT") ? (
          <fieldset className="flex flex-col gap-4">
            <legend className="text-sm font-medium text-ink">
              Target ranges
              <span className="mt-0.5 block text-xs font-normal text-ink-muted">
                Optional. Leave a pair empty to monitor without a range.
              </span>
            </legend>

            <div className="grid gap-4 sm:grid-cols-2">
              {asks("TEMPERATURE") ? (
                <>
                  <TextField
                    label={t("fieldMinTemp")}
                    inputMode="decimal"
                    placeholder={example("targetTempMinC")}
                    {...(errors.targetTempMinC?.message === undefined
                      ? {}
                      : { error: errors.targetTempMinC.message })}
                    {...register("targetTempMinC")}
                  />

                  <TextField
                    label={t("fieldMaxTemp")}
                    inputMode="decimal"
                    placeholder={example("targetTempMaxC")}
                    {...(errors.targetTempMaxC?.message === undefined
                      ? {}
                      : { error: errors.targetTempMaxC.message })}
                    {...register("targetTempMaxC")}
                  />
                </>
              ) : null}

              {asks("HUMIDITY") ? (
                <>
                  <TextField
                    label={t("fieldMinHumidity")}
                    inputMode="decimal"
                    placeholder={example("targetHumidityMin")}
                    {...(errors.targetHumidityMin?.message === undefined
                      ? {}
                      : { error: errors.targetHumidityMin.message })}
                    {...register("targetHumidityMin")}
                  />

                  <TextField
                    label={t("fieldMaxHumidity")}
                    inputMode="decimal"
                    placeholder={example("targetHumidityMax")}
                    {...(errors.targetHumidityMax?.message === undefined
                      ? {}
                      : { error: errors.targetHumidityMax.message })}
                    {...register("targetHumidityMax")}
                  />
                </>
              ) : null}

              {/* A radar level sensor measures downwards from above the contents,
                  so these two read backwards and the hints have to say which is
                  which. Naming them by the consequence is the whole reason the
                  labels are this long (ADR-060). */}
              {asks("DISTANCE") ? (
                <>
                  <TextField
                    label={t("fieldMinDistance")}
                    inputMode="numeric"
                    hint={t("hintDistanceTooFull")}
                    placeholder={example("targetDistanceMinMm")}
                    {...(errors.targetDistanceMinMm?.message === undefined
                      ? {}
                      : { error: errors.targetDistanceMinMm.message })}
                    {...register("targetDistanceMinMm")}
                  />

                  <TextField
                    label={t("fieldMaxDistance")}
                    inputMode="numeric"
                    hint={t("hintDistanceTooEmpty")}
                    placeholder={example("targetDistanceMaxMm")}
                    {...(errors.targetDistanceMaxMm?.message === undefined
                      ? {}
                      : { error: errors.targetDistanceMaxMm.message })}
                    {...register("targetDistanceMaxMm")}
                  />
                </>
              ) : null}

              {asks("CURRENT") ? (
                <>
                  <TextField
                    label={t("fieldMinCurrent")}
                    inputMode="decimal"
                    hint={t("hintCurrentLow")}
                    {...(errors.targetCurrentMinA?.message === undefined
                      ? {}
                      : { error: errors.targetCurrentMinA.message })}
                    {...register("targetCurrentMinA")}
                  />

                  <TextField
                    label={t("fieldMaxCurrent")}
                    inputMode="decimal"
                    hint={t("hintCurrentHigh")}
                    {...(errors.targetCurrentMaxA?.message === undefined
                      ? {}
                      : { error: errors.targetCurrentMaxA.message })}
                    {...register("targetCurrentMaxA")}
                  />
                </>
              ) : null}
            </div>
          </fieldset>
        ) : null}

        {/* Separate from the target ranges above, because it is not one: a
            range is a pair of numbers a reading is compared against, and
            this is a calendar. Only a presence sensor is judged by it
            (ADR-069). */}
        {asks("OCCUPANCY") ? (
          <fieldset className="flex flex-col gap-4">
            <legend className="mb-1 text-sm font-medium text-ink">{t("legendExpectedOccupancy")}</legend>

            <SelectField
              label={t("fieldWhenInUse")}
              hint={t("hintOccupancyHours")}
              {...(errors.occupancy?.message === undefined
                ? {}
                : { error: errors.occupancy.message })}
              value={occupancy}
              onChange={(value) =>
                setValue("occupancy", value as AssetFormValues["occupancy"], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              data={occupancyExpectationSchema.options.map((option) => ({
                value: option,
                label: OCCUPANCY_LABELS[option],
              }))}
            />

            {occupancy === "SCHEDULED" ? (
              <>
                <fieldset>
                  <legend className="mb-2 text-sm font-medium text-ink">{t("legendDays")}</legend>

                  <div className="flex flex-wrap gap-3">
                    {WEEKDAY_VALUES.map((day) => (
                      <label
                        key={day}
                        className="flex items-center gap-2 text-sm text-ink"
                        htmlFor={`occupancy-day-${day}`}
                      >
                        <input
                          id={`occupancy-day-${day}`}
                          type="checkbox"
                          value={day}
                          className="size-4 rounded border-border-subtle"
                          {...register("occupancyDays")}
                        />
                        {tCommon(`weekdays.${day}`)}
                      </label>
                    ))}
                  </div>

                  {errors.occupancyDays?.message === undefined ? null : (
                    <p className="mt-2 text-xs font-medium text-danger">
                      {errors.occupancyDays.message}
                    </p>
                  )}
                </fieldset>

                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Native time inputs, so the value the browser produces is
                    already the HH:MM the API stores: no parsing, and no
                    locale deciding whether 7pm is 19:00 or 07:00 PM. */}
                  <TextField
                    label={t("fieldInUseFrom")}
                    type="time"
                    {...(errors.occupancyStart?.message === undefined
                      ? {}
                      : { error: errors.occupancyStart.message })}
                    {...register("occupancyStart")}
                  />

                  <TextField
                    label={t("fieldUntil")}
                    type="time"
                    hint={t("hintOvernightShift")}
                    {...(errors.occupancyEnd?.message === undefined
                      ? {}
                      : { error: errors.occupancyEnd.message })}
                    {...register("occupancyEnd")}
                  />
                </div>
              </>
            ) : null}
          </fieldset>
        ) : null}

        <TextAreaField
          label={t("fieldNotes")}
          hint={t("hintAssetNotes")}
          {...(errors.notes?.message === undefined ? {} : { error: errors.notes.message })}
          {...register("notes")}
        />
      </div>
    </FormDialog>
  );
}
